const BLOCKED_TAGS = new Set([
  'script',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'link',
  'meta',
  'base',
  'style',
]);

const URL_ATTRIBUTES = new Set(['href', 'src', 'poster', 'action']);

const ALLOWED_ATTRIBUTES = new Set([
  'style',
  'class',
  'id',
  'name',
  'lang',
  'dir',
  'href',
  'src',
  'poster',
  'alt',
  'title',
  'target',
  'rel',
  'width',
  'height',
  'align',
  'valign',
  'cellpadding',
  'cellspacing',
  'colspan',
  'rowspan',
  'bgcolor',
  'border',
  'scope',
  'role',
  'aria-label',
  'aria-labelledby',
  'aria-hidden',
]);

const SAFE_TARGETS = new Set(['_self', '_blank', '_parent', '_top']);
const GUIDE_HTML_SCOPE_CLASS = 'guide-rich-html-scope';

const DANGEROUS_URL_PATTERN = /^(?:\s*)(?:javascript|vbscript|data:text\/html)/i;
const DANGEROUS_STYLE_PATTERN =
  /expression\s*\(|javascript\s*:|vbscript\s*:|behavior\s*:|-moz-binding|@import/i;
const SAFE_CLASS_TOKEN_PATTERN = /^[-_a-zA-Z0-9:]+$/;
const SAFE_IDENTIFIER_PATTERN = /^[-_a-zA-Z0-9:.]+$/;

const isSafeUrl = (value: string) => {
  const normalizedValue = String(value || '').trim();
  return normalizedValue === '' || !DANGEROUS_URL_PATTERN.test(normalizedValue);
};

const sanitizeStyleValue = (styleValue: string) => {
  const source = String(styleValue || '').trim();
  if (!source) return '';

  return source
    .split(';')
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => {
      const index = part.indexOf(':');
      if (index <= 0) return '';

      const property = part.slice(0, index).trim().toLowerCase();
      const value = part.slice(index + 1).trim();

      if (!property || !value) return '';
      if (!/^[-a-z]+$/.test(property)) return '';
      if (DANGEROUS_STYLE_PATTERN.test(value)) return '';
      if (/url\s*\(\s*['"]?\s*javascript:/i.test(value)) return '';

      return `${property}:${value}`;
    })
    .filter(Boolean)
    .join(';');
};

const sanitizeTokenList = (value: string) =>
  String(value || '')
    .split(/\s+/)
    .map(token => token.trim())
    .filter(token => token && SAFE_CLASS_TOKEN_PATTERN.test(token))
    .join(' ');

const sanitizeIdentifierValue = (value: string) => {
  const normalizedValue = String(value || '').trim();
  return SAFE_IDENTIFIER_PATTERN.test(normalizedValue) ? normalizedValue : '';
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const splitSelectorList = (selectorText: string) => {
  const selectors: string[] = [];
  let currentSelector = '';
  let bracketDepth = 0;
  let parenDepth = 0;
  let activeQuote = '';

  for (let index = 0; index < selectorText.length; index += 1) {
    const character = selectorText[index];

    if (activeQuote) {
      currentSelector += character;
      if (character === activeQuote && selectorText[index - 1] !== '\\') {
        activeQuote = '';
      }
      continue;
    }

    if (character === '"' || character === "'") {
      activeQuote = character;
      currentSelector += character;
      continue;
    }

    if (character === '[') bracketDepth += 1;
    if (character === ']') bracketDepth = Math.max(0, bracketDepth - 1);
    if (character === '(') parenDepth += 1;
    if (character === ')') parenDepth = Math.max(0, parenDepth - 1);

    if (character === ',' && !bracketDepth && !parenDepth) {
      if (currentSelector.trim()) selectors.push(currentSelector.trim());
      currentSelector = '';
      continue;
    }

    currentSelector += character;
  }

  if (currentSelector.trim()) selectors.push(currentSelector.trim());

  return selectors;
};

const normalizeScopedSelector = (
  selectorText: string,
  scopeSelector: string,
) => {
  let normalizedSelector = selectorText.trim();
  if (!normalizedSelector) return '';

  normalizedSelector = normalizedSelector.replace(
    /^\s*:root\b/i,
    scopeSelector,
  );
  normalizedSelector = normalizedSelector.replace(
    /^\s*html\b(?:\s*>\s*body\b|\s+body\b)?/i,
    scopeSelector,
  );
  normalizedSelector = normalizedSelector.replace(/^\s*body\b/i, scopeSelector);
  normalizedSelector = normalizedSelector.replace(
    new RegExp(
      `^\\s*${escapeRegExp(scopeSelector)}(?:\\s*>\\s*|\\s+)body\\b`,
      'i',
    ),
    scopeSelector,
  );

  if (normalizedSelector.startsWith(scopeSelector)) {
    return normalizedSelector;
  }

  return `${scopeSelector} ${normalizedSelector}`;
};

const scopeSelectorList = (selectorText: string, scopeSelector: string) =>
  splitSelectorList(selectorText)
    .map(selector => normalizeScopedSelector(selector, scopeSelector))
    .filter(Boolean)
    .join(', ');

const serializeCssRule = (rule: CSSRule, scopeSelector: string): string => {
  switch (rule.type) {
    case CSSRule.STYLE_RULE: {
      const styleRule = rule as CSSStyleRule;
      const selectors = scopeSelectorList(
        styleRule.selectorText,
        scopeSelector,
      );
      const declarations = sanitizeStyleValue(styleRule.style.cssText);
      return selectors && declarations ? `${selectors}{${declarations}}` : '';
    }

    case CSSRule.MEDIA_RULE: {
      const mediaRule = rule as CSSMediaRule;
      const nestedRules = Array.from(mediaRule.cssRules)
        .map(nestedRule => serializeCssRule(nestedRule, scopeSelector))
        .filter(Boolean)
        .join('');

      return nestedRules ? `@media ${mediaRule.media.mediaText}{${nestedRules}}` : '';
    }

    case CSSRule.SUPPORTS_RULE: {
      const supportsRule = rule as CSSSupportsRule;
      const nestedRules = Array.from(supportsRule.cssRules)
        .map(nestedRule => serializeCssRule(nestedRule, scopeSelector))
        .filter(Boolean)
        .join('');

      return nestedRules
        ? `@supports ${supportsRule.conditionText}{${nestedRules}}`
        : '';
    }

    case CSSRule.KEYFRAMES_RULE: {
      const keyframesRule = rule as CSSKeyframesRule;
      const frames = Array.from(keyframesRule.cssRules)
        .map(frame => serializeCssRule(frame, scopeSelector))
        .filter(Boolean)
        .join('');

      return frames ? `@keyframes ${keyframesRule.name}{${frames}}` : '';
    }

    case CSSRule.KEYFRAME_RULE: {
      const keyframeRule = rule as CSSKeyframeRule;
      const declarations = sanitizeStyleValue(keyframeRule.style.cssText);
      return declarations ? `${keyframeRule.keyText}{${declarations}}` : '';
    }

    default:
      return '';
  }
};

const sanitizeStyleSheetText = (
  styleSheetText: string,
  scopeSelector: string,
  document: Document,
) => {
  const source = String(styleSheetText || '').trim();
  if (!source || typeof window === 'undefined') return '';

  const styleElement = document.createElement('style');

  try {
    styleElement.textContent = source;
    document.head.appendChild(styleElement);

    const cssRules = Array.from(styleElement.sheet?.cssRules || []);

    return cssRules
      .map(rule => serializeCssRule(rule, scopeSelector))
      .filter(Boolean)
      .join('\n');
  } catch {
    return '';
  } finally {
    styleElement.remove();
  }
};

const collectScopedStyleBlocks = (document: Document) => {
  const styleBlocks = Array.from(document.querySelectorAll('style'));
  const scopedStyles = styleBlocks
    .map(styleBlock =>
      sanitizeStyleSheetText(
        styleBlock.textContent || '',
        `.${GUIDE_HTML_SCOPE_CLASS}`,
        document,
      ),
    )
    .filter(Boolean);

  styleBlocks.forEach(styleBlock => styleBlock.remove());

  return scopedStyles.join('\n');
};

const sanitizeElementAttributes = (element: Element) => {
  Array.from(element.attributes).forEach(attribute => {
    const name = attribute.name.toLowerCase();

    if (name.startsWith('on')) {
      element.removeAttribute(attribute.name);
      return;
    }

    const isAllowedDataOrAria =
      name.startsWith('data-') || name.startsWith('aria-');

    if (!ALLOWED_ATTRIBUTES.has(name) && !isAllowedDataOrAria) {
      element.removeAttribute(attribute.name);
      return;
    }

    if (name === 'style') {
      const safeStyle = sanitizeStyleValue(attribute.value);
      if (!safeStyle) {
        element.removeAttribute(attribute.name);
      } else {
        element.setAttribute('style', safeStyle);
      }
      return;
    }

    if (name === 'class') {
      const safeClassNames = sanitizeTokenList(attribute.value);
      if (!safeClassNames) {
        element.removeAttribute(attribute.name);
      } else {
        element.setAttribute('class', safeClassNames);
      }
      return;
    }

    if (name === 'id' || name === 'name') {
      const safeIdentifier = sanitizeIdentifierValue(attribute.value);
      if (!safeIdentifier) {
        element.removeAttribute(attribute.name);
      } else {
        element.setAttribute(name, safeIdentifier);
      }
      return;
    }

    if (name === 'target') {
      const target = String(attribute.value || '').toLowerCase();
      if (!SAFE_TARGETS.has(target)) {
        element.removeAttribute(attribute.name);
      }
      return;
    }

    if (name === 'rel' && element.getAttribute('target') === '_blank') {
      element.setAttribute('rel', 'noopener noreferrer');
      return;
    }

    if (URL_ATTRIBUTES.has(name) && !isSafeUrl(attribute.value)) {
      element.removeAttribute(attribute.name);
    }
  });

  if (
    element.getAttribute('target') === '_blank' &&
    !element.getAttribute('rel')
  ) {
    element.setAttribute('rel', 'noopener noreferrer');
  }
};

const sanitizeNodeTree = (root: ParentNode) => {
  Array.from(root.children).forEach(element => {
    const tagName = element.tagName.toLowerCase();

    if (BLOCKED_TAGS.has(tagName)) {
      element.remove();
      return;
    }

    sanitizeElementAttributes(element);
    sanitizeNodeTree(element);
  });
};

export const sanitizeGuideHtml = (value: string) => {
  if (!value) return '';

  if (typeof window === 'undefined' || !window.DOMParser) {
    return value;
  }

  const parser = new window.DOMParser();
  const document = parser.parseFromString(value, 'text/html');
  const scopedStyles = collectScopedStyleBlocks(document);

  sanitizeElementAttributes(document.body);
  sanitizeNodeTree(document.body);

  const wrapper = document.createElement('div');
  Array.from(document.body.attributes).forEach(attribute => {
    if (attribute.name.toLowerCase() === 'class') return;
    wrapper.setAttribute(attribute.name, attribute.value);
  });

  const bodyClassNames = document.body.getAttribute('class');
  wrapper.setAttribute(
    'class',
    [GUIDE_HTML_SCOPE_CLASS, bodyClassNames].filter(Boolean).join(' '),
  );
  wrapper.innerHTML = document.body.innerHTML;

  if (!scopedStyles) {
    return wrapper.outerHTML;
  }

  const styleTag = document.createElement('style');
  styleTag.textContent = scopedStyles;

  return `${styleTag.outerHTML}${wrapper.outerHTML}`;
};
